import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import axiosBaseURL from "..";
import { message } from "antd";
import { CooperativeQueryOptions } from "../../types/cooperative";

export const useAddressList = () => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["address list"],
    staleTime: 120,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/address/region`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
          // Authorization: authString,
        },
      });

      return result;
    },
  });
};

export const useAddressById = (regionId: string) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["useAddressById", regionId],
    staleTime: 120,
    enabled: false,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/address/region/${regionId}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useListofWoredasByZoneId = (zoneId: string) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["useListofWoredasByZoneId", zoneId],
    staleTime: 120,
    enabled: false,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/address/woreda/${zoneId}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useCreateAddress = () => {
  const { user } = useAuth() as AuthContextValue;
  const { refetch } = useAddressList();
  return useMutation({
    mutationFn: async (address: any) => {
      const result = await axiosBaseURL.post(`/address/create`, address, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      refetch();
      return result;
    },
    onSuccess: () => {
      refetch();
    },
    onError: (error: any) => {
      console.error("Address create failed:", error);
      message.error("Failed to create address. Check the input or server.");
    },
  });
};

export const useUpdateAddress = () => {
  const { user } = useAuth() as AuthContextValue;
  const { refetch } = useAddressList();
  return useMutation({
    mutationFn: async (data: any) => {
      const result = await axiosBaseURL.patch(
        `/address/update/${data.regionId}`,
        data.address,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      refetch();
      return result;
    },
  });
};

export const useDeleteAddress = () => {
  const { user } = useAuth() as AuthContextValue;
  const { refetch } = useUnionList();
  return useMutation({
    mutationFn: async (woredaId: string) => {
      const result = await axiosBaseURL.delete(`/address/delete/{woredaId}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      refetch();
      return result;
    },
  });
};

// union api  calls
// export const useUnionList = (options?: {
//   search?: string;
//   status?: string;
//   page?: number;
//   size?: number;
// }, p0?: { enabled: boolean; }) => {
//   const { user } = useAuth() as AuthContextValue;

//   return useQuery({
//     queryKey: [
//       "union-list",
//       options?.search,
//       options?.status,
//       options?.page,
//       options?.size,
//     ].filter(Boolean),
//     staleTime: 120_000,
//     queryFn: async () => {
//       const queryParams = new URLSearchParams();

//       if (options?.page !== undefined)
//         queryParams.append("page", options.page.toString());
//       if (options?.size !== undefined)
//         queryParams.append("size", options.size.toString());
//       if (options?.status) queryParams.append("status", options.status); // API expects uppercase
//       if (options?.search) queryParams.append("search", options.search);

//       const url = `/unions?${queryParams.toString()}`;

//       const { data } = await axiosBaseURL.get(url, {
//         headers: { header: `${user?.userId}/${user?.token}` },
//       });

//       return data;
//     },
//     enabled: !!user?.mfiId,
//   });
// };
interface UseUnionListOptions {
  search?: string;
  status?: string;
  page?: number;
  size?: number;
  enabled?: boolean;
}

export const useUnionList = (options?: UseUnionListOptions) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "union-list",
      options?.search,
      options?.status,
      options?.page,
      options?.size,
    ].filter(Boolean),

    staleTime: 120_000,

    enabled: !!user?.mfiId && (options?.enabled ?? true),

    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.status) queryParams.append("status", options.status);
      if (options?.search) queryParams.append("search", options.search);

      const url = `/unions?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useCreateUnion = () => {
  const { user } = useAuth() as AuthContextValue;
  const { refetch } = useUnionList();
  return useMutation({
    mutationFn: async (union: any) => {
      const result = await axiosBaseURL.post(`/unions`, union, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      refetch();
      return result;
    },
  });
};

export const useUpdateUnion = () => {
  const { user } = useAuth() as AuthContextValue;
  const { refetch } = useUnionList();
  return useMutation({
    mutationFn: async (data: any) => {
      const result = await axiosBaseURL.put(`/unions/${data.id}`, data.union, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      refetch();
      return result;
    },
  });
};

export const useDeleteUnion = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  const { refetch } = useUnionList();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await axiosBaseURL.delete(`/unions/${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      refetch();
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["union-list"] });
    },
  });
};

//  cooperatives endpoint
export const useGetCooperativesByUnionId = (
  unionId?: number | null,
  options?: CooperativeQueryOptions
) => {
  const { user } = useAuth() as AuthContextValue;

  // Determine the query key based on usage
  const queryKey = unionId
    ? [
        "cooperativesByUnionId",
        unionId,
        options?.search,
        options?.page,
        options?.size,
        options?.status,
      ]
    : [
        "cooperatives",
        options?.search,
        options?.page,
        options?.size,
        options?.status,
      ];

  // Determine the URL based on usage
  const getUrl = () => {
    const queryParams = new URLSearchParams();

    // Add query parameters if provided
    if (options?.search) queryParams.append("search", options.search);
    if (options?.page !== undefined)
      queryParams.append("page", options.page.toString());
    if (options?.size !== undefined)
      queryParams.append("size", options.size.toString());
    if (options?.status) queryParams.append("status", options.status);

    const queryString = queryParams.toString();

    if (unionId) {
      return `/unions/${unionId}/cooperatives${
        queryString ? `?${queryString}` : ""
      }`;
    }

    return `/cooperatives${queryString ? `?${queryString}` : ""}`;
  };

  return useQuery({
    queryKey: queryKey.filter(Boolean),
    staleTime: 120_000,
    enabled: options?.enabled ?? (unionId ? !!unionId : true),
    queryFn: async () => {
      const url = getUrl();
      const { data } = await axiosBaseURL.get(url, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });
      return data;
    },
  });
};

export const useGetCooperativesByUnionIdX = (unionId: number | null) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: ["useCooperativesByUnionId", unionId],
    staleTime: 120,
    enabled: !!unionId,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/unions/${unionId}/cooperatives`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useCreateCooperative = (unionId: number) => {
  const { user } = useAuth() as AuthContextValue;
  const { refetch } = useGetCooperativesByUnionId(unionId);
  return useMutation({
    mutationFn: async (cooperative: any) => {
      const result = await axiosBaseURL.post(
        `/unions/${unionId}/cooperatives`,
        cooperative,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      refetch();
      return result;
    },
  });
};

export const useUpdateCooperative = (unionId: number) => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const result = await axiosBaseURL.put(
        `/unions/cooperatives/${data.id}`,
        data.cooperative,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cooperativesByUnionId"] });
    },
  });
};

export const useDeleteCooperative = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await axiosBaseURL.delete(`/unions/cooperatives/${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cooperativesByUnionId"] });
    },
  });
};

export const useCreateAgriFinance = () => {
  const { user } = useAuth() as AuthContextValue;
  return useMutation({
    mutationFn: async (agriFinanceData: any) => {
      const result = await axiosBaseURL.post("/agriFinance", agriFinanceData, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });
      return result;
    },
    onSuccess: () => {
      message.success("AgriFinance data submitted successfully.");
    },
    onError: (error: any) => {
      console.error("AgriFinance submission failed:", error);
      message.error("Failed to submit AgriFinance data. Please try again.");
    },
  });
};
